import { useEffect, useMemo, useState } from 'react'
import { api } from '../../tools/request'
import { Table, Space, Typography } from '@/UI'
import { Link, useHistory, useParams } from 'react-router-dom'
import { ArrowRightOutlined } from '@ant-design/icons'
import jMoment from 'moment-jalaali'

const { Title } = Typography

const DEFAULT_PAGE_SIZE = 10

export default function WalletDetail() {
  const [data, setData] = useState({ items: [] })
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const { id, fromDate, toDate } = useParams()
  const history = useHistory()

  const columns = useMemo(() => {
    return getColumnListByServiceCode(id, data)
  }, [id, data])

  useEffect(() => {
    setLoading(true)
    api('/billing/transaction-detail/fetch-transaction-detail', {
      method: 'POST',
      data: {
        fromDate,
        toDate,
        serviceCode: id,
        page,
        size: pageSize
      }
    })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false))
  }, [page, pageSize])

  return (
    <div>
      <Space style={{ marginBottom: '1rem' }}>
        <ArrowRightOutlined
          onClick={history.goBack}
          style={{
            fontSize: '1.5rem',
            margin: '1rem',
            color: 'black',
            paddingTop: '.7rem'
          }}
        />
        <Title level={2}>{data.serviceName}</Title>
        <Title level={5} style={{ color: '#14141499' }}>
          از {convertDate(fromDate)} تا {convertDate(toDate)}
        </Title>
      </Space>
      <Table
        data={data.items}
        columns={columns}
        loading={loading}
        pagination={{
          total: data.totalCount,
          pageSize: pageSize,
          current: page,
          onChange: (e) => setPage(e),
          onShowSizeChange: (e, size) => setPageSize(size)
        }}
      />
    </div>
  )
}

function convertDate(date: string) {
  return jMoment(date, 'YYYY-M-D').locale('fa').format('jD jMMMM jYYYY')
}

const SERVICE_CODES = [
  {
    items: [
      'fulfillment:digikala-items',
      'fulfillment:1st-item',
      'fulfillment:other-items',
      'delivery:digikala',
      'consumable:box',
      'delivery:on-demand',
      'delivery:post',
      'delivery:ffc',
      'delivery+cod',
      'delivery:scheduled',
      'delivery:freight',
      'outbound:included-items',
      'outbound:label',
      'delivery:scheduled:gt5',
      'delivery:scheduled:lte5',
      'outbound:box',
      'outbound:included-items:exclusive',
      'fulfillment:loading',
      'fulfillment:order-items',
      'delivery:scheduled:gt5t10',
      'fulfillment:order',
      'fulfillment:package',
      'fulfillment:vip:1st-item',
      'fulfillment:vip:other-items',
      'delivery:ronad',
      'outbound:safety',
      'delivery:snapshop',
      'delivery:tehran:3hr',
      'delivery:snapshop-items',
      'delivery:ffc-vip-process',
      'fulfillment:fast:order-items'
    ],
    columns: (data) => [
      { key: 'effectiveDate', title: 'تاریخ', render: convertDate },
      {
        key: 'qty',
        title: renderColumnHead('تعداد', data.unit),
        render: (f) => +f
      },
      {
        key: 'objId',
        title: 'شناسه سفارش',
        render: (f) => <Link to={`/orders/${f}`}>{f}</Link>
      },
      { key: 'description', title: 'توضیحات', render: (f) => f || '-' }
    ]
  },
  {
    items: [
      'storage:sku-based',
      'storage:temporary:box',
      'storage:temporary:item'
    ],
    columns: (data) => [
      { key: 'effectiveDate', title: 'تاریخ', render: convertDate },
      {
        key: 'qty',
        title: renderColumnHead('فضای استفاده شده', data.unit),
        render: (f) => +f
      },
      { key: 'description', title: 'توضیحات', render: (f) => f || '-' }
    ]
  },
  {
    items: ['storage:constant'],
    columns: (data) => [
      { key: 'effectiveDate', title: 'تاریخ', render: convertDate },
      {
        key: 'qty',
        title: renderColumnHead('فضای رزرو شده', data.unit),
        render: (f) => +f
      },
      { key: 'description', title: 'توضیحات', render: (f) => f || '-' }
    ]
  },
  {
    items: [
      'storage:stocktaking',
      'paneladministration'
    ],
    columns: (data) => [
      { key: 'effectiveDate', title: 'تاریخ', render: convertDate },
      { key: 'description', title: 'توضیحات', render: (f) => f || '-' }
    ]
  },
  {
    items: [
      'inbound:shrink-wrap',
      'inbound:basic',
      'inbound:identification+qc',
      'inbound:vip',
      'inbound:label',
      'inbound:kitting:lte5',
      'inbound:kitting:gt5',
      'inbound:bundling',
      'inbound:safety',
      'inbound:qc:exclusive',
      'inbound:qc:vip',
      'pickup',
      'inbound:assembling',
      'inbound:identification:sku-based'
    ],
    columns: (data) => [
      { key: 'effectiveDate', title: 'تاریخ', render: convertDate },
      {
        key: 'qty',
        title: renderColumnHead('تعداد', data.unit),
        render: (f) => +f
      },
      {
        key: 'objId',
        title: 'شناسه محموله',
        render: (f) => <Link to={`/parcels/details/${f}`}>{f}</Link>
      },
      { key: 'description', title: 'توضیحات', render: (f) => f || '-' }
    ]
  }
]

function getColumnListByServiceCode(serviceCode: string, data: any) {
  const record = SERVICE_CODES.find(({ items }) => items.includes(serviceCode))

  return record?.columns(data)
}

function renderColumnHead(title: string, unit: string) {
  return (
    <div>
      {title}{' '}
      <span style={{ fontSize: '12px', fontWeight: 500, color: '#00000073' }}>
        {unit}
      </span>
    </div>
  )
}
